import { Schema, z } from 'zod';
import {
  ModuleConfiguration,
  ModuleConfigurationSchema
} from './module-configuration';

export type Schedule = {
  readonly startTime?: string;
  readonly endTime?: string;
};

export const ScheduleSchema: Schema<Schedule> = z.object({
  startTime: z.string().optional(),
  endTime: z.string().optional()
});

export type VariantCase = {
  readonly variant: string | boolean;
  readonly value?: ModuleConfiguration;
};

export const VariantCaseSchema: Schema<VariantCase> = z.object({
  variant: z.union([z.string(), z.boolean()]),
  value: ModuleConfigurationSchema.optional()
});

export const EXPERIMENT_TYPE_VALUE = 'experiment';
export type ExperimentType = typeof EXPERIMENT_TYPE_VALUE;

export const ExperimentTypeSchema: Schema<ExperimentType> = z.literal(
  EXPERIMENT_TYPE_VALUE
);

export const PERSONALIZATION_TYPE_VALUE = 'personalization';
export type PersonalizationType = typeof PERSONALIZATION_TYPE_VALUE;

export const PersonalizationTypeSchema: Schema<PersonalizationType> = z.literal(
  PERSONALIZATION_TYPE_VALUE
);

export type ExperimentTarget = {
  readonly id: string;
  readonly type: 'string' | 'boolean';
};

export const ExperimentTargetSchema: Schema<ExperimentTarget> = z.object({
  id: z.string(),
  type: z.union([z.literal('string'), z.literal('boolean')])
});

export type Experiment = {
  readonly type: ExperimentType;
  readonly target: ExperimentTarget;
  readonly label: string;
  readonly schedule?: Schedule;
  readonly cases: Readonly<VariantCase[]>;
};

export const ExperimentSchema: Schema<Experiment> = z.object({
  type: ExperimentTypeSchema,
  target: ExperimentTargetSchema,
  label: z.string(),
  schedule: ScheduleSchema.optional(),
  cases: z.array(VariantCaseSchema)
});

export type PersonalizationTarget = {
  readonly id: string;
  readonly entityType:
    | 'AJS_ANONYMOUS_USER'
    | 'ATLASSIAN_ACCOUNT'
    | 'EMAIL_UUID'
    | 'ORG'
    | 'SITE'
    | 'SITE_USER'
    | 'TRELLO_USER'
    | 'CUSTOMER_DOMAIN';
  readonly type: 'string' | 'boolean';
};

export const PersonalizationTargetSchema: Schema<PersonalizationTarget> =
  z.object({
    id: z.string(),
    entityType: z.union([
      z.literal('AJS_ANONYMOUS_USER'),
      z.literal('ATLASSIAN_ACCOUNT'),
      z.literal('EMAIL_UUID'),
      z.literal('ORG'),
      z.literal('SITE'),
      z.literal('SITE_USER'),
      z.literal('TRELLO_USER'),
      z.literal('CUSTOMER_DOMAIN')
    ]),
    type: z.union([z.literal('string'), z.literal('boolean')])
  });

export type Personalization = {
  readonly type: PersonalizationType;
  readonly target: PersonalizationTarget;
  readonly label: string;
  readonly schedule?: Schedule;
  readonly cases: Readonly<VariantCase[]>;
};

export const PersonalizationSchema: Schema<Personalization> = z.object({
  type: PersonalizationTypeSchema,
  target: PersonalizationTargetSchema,
  label: z.string(),
  schedule: ScheduleSchema.optional(),
  cases: z.array(VariantCaseSchema)
});

export type Variant = Experiment | Personalization;

export const VariantSchema: Schema<Variant> = z.union([
  ExperimentSchema,
  PersonalizationSchema
]);
